/**
 * Reown AppKit — wallet connection and the account modal.
 *
 * AppKit handles *connection*: which wallet, which chain, the modal UI. It does
 * not by itself establish a session with our API. That is the job of the SIWE
 * exchange in `useAuth`, which takes the connected account and turns it into an
 * httpOnly cookie. Connected and signed-in are two different states, and the
 * header reflects that.
 */
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { createAppKit } from '@reown/appkit/react'
import { defineChain } from '@reown/appkit/networks'
import { arbitrum, base, mainnet } from '@reown/appkit/networks'

const projectId = import.meta.env.VITE_REOWN_PROJECT_ID as string | undefined

/**
 * Whether a real Reown project id was configured. The app is fully readable
 * without one — packs, odds, the market, the leaderboard, the fairness scheme —
 * so a missing id must degrade to "you cannot sign in yet", never to a blank
 * page. The connect button checks this and explains itself.
 */
export const walletReady = Boolean(projectId && projectId.length > 8)

/**
 * Robinhood Chain, defined here because AppKit does not ship it.
 *
 * Registered so a wallet can actually be *switched* to it: the treasury is the
 * same address on every chain, so a deposit meant for this one that is sent
 * from a wallet still pointed at Ethereum lands on Ethereum instead. The funds
 * are not lost — the Ethereum scanner credits them — but the user asked for one
 * chain and used another, and the only way to hold them to the right one is to
 * be able to prompt the switch.
 */
export const robinhoodChain = defineChain({
  id: 4663,
  caipNetworkId: 'eip155:4663',
  chainNamespace: 'eip155',
  name: 'Robinhood Chain',
  // An Orbit rollup with no token of its own; gas is ether.
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: ['https://rpc.mainnet.chain.robinhood.com'] } },
  blockExplorers: {
    default: { name: 'Blockscout', url: 'https://robinhoodchain.blockscout.com' },
  },
})

/** Arbitrum first: Robinhood Chain is an Arbitrum Orbit rollup. */
export const networks = [arbitrum, mainnet, base, robinhoodChain] as const

const metadata = {
  name: 'Elyxr',
  description: 'Rip packs of real equities tokens. Published odds, provable rolls.',
  url: (import.meta.env.VITE_APP_URL as string | undefined) ?? window.location.origin,
  icons: [`${window.location.origin}/brand/exlyr-mark-256.png`],
}

export const wagmiAdapter = new WagmiAdapter({
  networks: [...networks],
  projectId: projectId ?? 'missing-project-id',
  ssr: false,
})

export const wagmiConfig = wagmiAdapter.wagmiConfig

/**
 * Always initialised, even without a configured id.
 *
 * `useAppKit()` and `useDisconnect()` throw outright if `createAppKit` was never
 * called, and React hooks cannot be called conditionally — so skipping this when
 * the id is missing takes down every component that touches auth, which is the
 * header, and with it the whole page. Initialising with a placeholder keeps the
 * hooks valid; `walletReady` is what actually gates opening the modal, so the
 * failure stays contained to the one button that cannot work anyway.
 */
createAppKit({
  adapters: [wagmiAdapter],
  networks: [...networks],
  defaultNetwork: arbitrum,
  projectId: projectId || 'exlyr-unconfigured',
  metadata,
  features: {
    analytics: false,
    // Wallet-only sign-in, deliberately. Email/social login provisions an
    // embedded wallet the visitor never chose and likely doesn't recognise as
    // one — fine for browsing, but deposits are attributed by the sender of an
    // on-chain transfer and withdrawals return to this same address, so an
    // account nobody knows how to reach on-chain is a support problem waiting
    // to happen the moment settlement is real. Revisit once there's an
    // in-app path for a confused embedded-wallet user to actually get funds
    // out.
    email: false,
    socials: false,
  },
  themeMode: 'dark',
  themeVariables: {
    '--w3m-accent': '#30F810',
    '--w3m-color-mix': '#000000',
    '--w3m-color-mix-strength': 20,
    '--w3m-border-radius-master': '0px',
    '--w3m-font-family': "'Inter', system-ui, sans-serif",
  },
})
